import z from 'zod'
import { DocumentReference } from 'firebase/firestore'
import { GetEntitySchema } from './network'
import { ClientBaseSchema, ContactBaseSchema } from './client'

export const StatusEventSchema = z.object({
  uid: z.string(),
  name: z
    .string()
    .min(1, 'Estado debe tener al menos 1 caracter')
    .max(50, 'Estado debe tener como máximo 50 caracteres')
    .trim(),
  color: z
    .string()
    .min(4, 'Color debe tener al menos 4 caracteres')
    .max(7, 'Color debe tener como máximo 7 caracteres')
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Color debe ser un código hexadecimal válido'),
  order: z.number().positive('Orden debe ser un número positivo'),
  createdAt: z.number(),
  updatedAt: z.number(),
  deleted: z.boolean().optional(),
})

export type StatusEventType = z.infer<typeof StatusEventSchema>

export const EventTypeSchema = z.object({
  uid: z.string(),
  name: z
    .string()
    .min(3, 'Nombre debe tener al menos 3 caracteres')
    .max(50, 'Nombre debe tener como máximo 50 caracteres')
    .trim(),
  color: z
    .string()
    .min(4, 'Color debe tener al menos 4 caracteres')
    .max(7, 'Color debe tener como máximo 7 caracteres')
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Color debe ser un código hexadecimal válido'),
  createdAt: z.number(),
  updatedAt: z.number(),
  deleted: z.boolean().optional(),
})

export type EventTypeType = z.infer<typeof EventTypeSchema>

export const EventBaseSchema = z.object({
  uid: z.string(),
  name: z
    .string()
    .min(3, 'Nombre debe tener al menos 3 caracteres')
    .max(50, 'Nombre debe tener como máximo 50 caracteres')
    .trim(),
  amount: z.number().positive('Cantidad debe ser mayor a 0').optional(),
  description: z.string().max(500, 'Descripción debe tener como máximo 500 caracteres').trim(),
  date: z.number(),
  endDate: z.number(),
  deleted: z.boolean().optional(),
  attachments: z.array(z.string()).max(4, 'Máximo 4 imágenes'),
  createdAt: z.number(),
  updatedAt: z.number(),
  client: z.lazy(() =>
    ClientBaseSchema.pick({
      uid: true,
      name: true,
      ruc: true,
    }),
  ),
  contacts: z.array(
    z.lazy(() =>
      ContactBaseSchema.pick({
        uid: true,
        name: true,
        email: true,
        phone: true,
        master: true,
      }),
    ),
  ),
  type: EventTypeSchema,
  status: StatusEventSchema,
})

export type EventBaseType = z.infer<typeof EventBaseSchema>

// Internal use
export const EventFirebaseSchema = EventBaseSchema.omit({
  type: true,
  status: true,
  client: true,
}).extend({
  client: z.custom<DocumentReference>(),
  type: z.custom<DocumentReference>(),
  status: z.custom<DocumentReference>(),
})

export type EventFirebaseType = z.infer<typeof EventFirebaseSchema>

// Create
export const CreateEventSchema = z.object({
  clientID: z.string(),
  data: EventBaseSchema.omit({
    uid: true,
    deleted: true,
    createdAt: true,
    updatedAt: true,
  }),
})

export const CreateEventStatusSchema = StatusEventSchema.omit({
  uid: true,
  createdAt: true,
  updatedAt: true,
  deleted: true,
})

export type CreateEventStatusType = z.infer<typeof CreateEventStatusSchema>

export type CreateEventType = z.infer<typeof CreateEventSchema>

// Update
export const UpdateEventSchema = z.object({
  clientID: z.string(),
  data: EventBaseSchema.omit({
    deleted: true,
    createdAt: true,
    updatedAt: true,
  }).partial(),
})

export type UpdateEventType = z.infer<typeof UpdateEventSchema>

export const UpdateEventStatusSchema = StatusEventSchema.omit({
  createdAt: true,
  updatedAt: true,
  deleted: true,
})

export type UpdateEventStatusType = z.infer<typeof UpdateEventStatusSchema>

// Delete
export const DeleteEventsSchema = z.object({
  clientID: z.string().nullish(),
  uids: z.array(z.string()),
})

export type DeleteEventsType = z.infer<typeof DeleteEventsSchema>

// Search
export const SearchEventSchema = GetEntitySchema.extend({
  startDate: z.number().optional(),
  endDate: z.number().optional(),
})

export type SearchEventType = z.infer<typeof SearchEventSchema>

// Get events
export const GetEventsSchema = GetEntitySchema.extend({
  clientID: z.string().nullish(),
})

export type GetEventsType = z.infer<typeof GetEventsSchema>

// Event type
export const CreateEventTypeSchema = EventTypeSchema.omit({
  uid: true,
  createdAt: true,
  updatedAt: true,
  deleted: true,
})

export type CreateEventTypeType = z.infer<typeof CreateEventTypeSchema>

// Update event type
export const UpdateEventTypeSchema = EventTypeSchema

export type UpdateEventTypeType = z.infer<typeof UpdateEventTypeSchema>
